/**
 * Chiffrement de l'instantané hors ligne (M11) : AES-GCM 256, clé Web Crypto
 * NON EXPORTABLE, IV aléatoire de 12 octets à chaque écriture, données
 * additionnelles = l'identifiant `<compteId>:<slug>` — un instantané recopié
 * sous un autre identifiant ne se déchiffre pas.
 *
 * Le contenu métier est chiffré dans IndexedDB ; les identifiants restent
 * visibles. Une clé non exportable interdit son export via Web Crypto, sans
 * protéger contre du code de même origine ni un navigateur déverrouillé.
 * Détruire la clé rend le chiffré restant illisible par cette application.
 */
export interface Chiffre {
  readonly iv: Uint8Array<ArrayBuffer>;
  readonly donnees: ArrayBuffer;
}

const ALGORITHME = { name: 'AES-GCM', length: 256 } as const;

export async function creerCle(moteur: Crypto): Promise<CryptoKey> {
  return moteur.subtle.generateKey(ALGORITHME, false, ['encrypt', 'decrypt']);
}

export async function chiffrer(
  moteur: Crypto,
  cle: CryptoKey,
  aad: string,
  valeur: unknown,
): Promise<Chiffre> {
  const iv = moteur.getRandomValues(new Uint8Array(12));
  const clair = new TextEncoder().encode(JSON.stringify(valeur));
  const donnees = await moteur.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(aad) },
    cle,
    clair,
  );
  return { iv, donnees };
}

export async function dechiffrer(
  moteur: Crypto,
  cle: CryptoKey,
  aad: string,
  chiffre: Chiffre,
): Promise<unknown> {
  const clair = await moteur.subtle.decrypt(
    { name: 'AES-GCM', iv: chiffre.iv, additionalData: new TextEncoder().encode(aad) },
    cle,
    chiffre.donnees,
  );
  return JSON.parse(new TextDecoder().decode(clair)) as unknown;
}
